/**
 * Rateio de um lançamento entre categorias — a conta é feita em CENTAVOS,
 * como no backend: a soma das partes precisa fechar o valor do lançamento
 * exatamente (0,10 + 0,20 tem de dar 0,30, e não 0,30000000000000004).
 *
 * As partes vivem na tela como texto de input (`amountInput`); o payload
 * enviado à API leva o valor em reais POSITIVO — o sinal é o do lançamento.
 * Usado pelos dois modais gêmeos (conta corrente e fatura de cartão).
 */
import type { FinancialAllocation, FinancialAllocationRequest } from '@/types/financial';
import { formatCurrencyBRL } from './financialUtils';
import { toCents, centsToInput } from './installmentCalc';

export interface AllocationLine {
  /** Chave de render — as partes ainda não têm id antes de salvar. */
  tempId: string;
  accountingPlanErpId: number | null;
  /** Texto do input ("1.234,56" ou "1234.56"); vazio = 0. */
  amountInput: string;
  note: string;
}

let tempSeq = 0;

export const newAllocationLine = (): AllocationLine => ({
  tempId: `alloc-${++tempSeq}`,
  accountingPlanErpId: null,
  amountInput: '',
  note: '',
});

/** Centavos de uma parte; texto vazio/inválido conta como 0. */
export const allocationLineCents = (line: AllocationLine): number => {
  if (!line.amountInput.trim()) return 0;
  return toCents(line.amountInput) ?? 0;
};

export const allocationsTotalCents = (lines: AllocationLine[]): number =>
  lines.reduce((sum, line) => sum + allocationLineCents(line), 0);

/** O que ainda falta distribuir (negativo = as partes passaram do total). */
export const remainingCents = (totalCents: number, lines: AllocationLine[]): number =>
  totalCents - allocationsTotalCents(lines);

const brl = (cents: number): string => formatCurrencyBRL(cents / 100);

/**
 * Mensagem do que impede salvar o rateio; null = válido. As regras são as
 * mesmas que o backend aplica — a tela só as antecipa. A ordem importa: o
 * usuário vê primeiro o que é estrutural (quantidade de partes) e por último
 * a conta que não fecha.
 */
export const allocationsValidationMessage = (totalCents: number, lines: AllocationLine[]): string | null => {
  if (totalCents <= 0) return 'Informe o valor do lançamento antes de ratear';
  if (lines.length < 2) return 'O rateio exige ao menos 2 categorias';
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].accountingPlanErpId == null) return `Escolha a categoria da parte ${i + 1}`;
  }
  for (let i = 0; i < lines.length; i++) {
    if (allocationLineCents(lines[i]) <= 0) return `Informe o valor da parte ${i + 1}`;
  }
  const rest = remainingCents(totalCents, lines);
  if (rest > 0) return `Faltam ${brl(rest)} para fechar ${brl(totalCents)}`;
  if (rest < 0) return `Sobram ${brl(-rest)} acima de ${brl(totalCents)}`;
  return null;
};

/**
 * Joga o restante (quando positivo) na ÚLTIMA parte sem valor; se todas já
 * têm valor, soma na última. Sem restante a distribuir, devolve as mesmas
 * linhas (mesma referência — a tela não re-renderiza à toa).
 */
export const distributeRemaining = (totalCents: number, lines: AllocationLine[]): AllocationLine[] => {
  const rest = remainingCents(totalCents, lines);
  if (rest <= 0 || lines.length === 0) return lines;
  let target = -1;
  for (let i = lines.length - 1; i >= 0; i--) {
    if (allocationLineCents(lines[i]) === 0) {
      target = i;
      break;
    }
  }
  if (target < 0) target = lines.length - 1;
  return lines.map((line, i) =>
    i === target ? { ...line, amountInput: centsToInput(allocationLineCents(line) + rest) } : line,
  );
};

/** Categoria da maior parte — é a que o pai grava como principal. Empate: a primeira. */
export const principalErpId = (lines: AllocationLine[]): number | null => {
  let best: AllocationLine | null = null;
  let bestCents = -1;
  for (const line of lines) {
    if (line.accountingPlanErpId == null) continue;
    const cents = allocationLineCents(line);
    if (cents > bestCents) {
      best = line;
      bestCents = cents;
    }
  }
  return best?.accountingPlanErpId ?? null;
};

/** Partes para o request: valor em reais, positivo; nota sem espaços ou ausente. */
export const toAllocationPayload = (lines: AllocationLine[]): FinancialAllocationRequest[] =>
  lines.map((line) => {
    const note = line.note.trim();
    return {
      accountingPlanErpId: line.accountingPlanErpId ?? 0,
      amount: allocationLineCents(line) / 100,
      ...(note ? { note } : {}),
    };
  });

/** Partes vindas da API (com sinal) → linhas da tela (sempre positivas). */
export const linesFromAllocations = (allocs: FinancialAllocation[] | undefined): AllocationLine[] =>
  (allocs ?? []).map((a) => ({
    ...newAllocationLine(),
    accountingPlanErpId: a.accountingPlanErpId,
    amountInput: centsToInput(Math.round(Math.abs(a.amount) * 100)),
    note: a.note ?? '',
  }));

/** "Malas R$ 3.000,00 · Capa R$ 1.967,50" — resumo de uma linha para tooltips/busca. */
export const allocationSummary = (allocs: FinancialAllocation[]): string =>
  allocs
    .map((a) => `${a.categoryLabel ?? a.accountingPlanErpId} ${formatCurrencyBRL(Math.abs(a.amount))}`)
    .join(' · ');
